import { authApi } from '@/lib/api'
import { createFileRoute } from '@tanstack/react-router'
import { AlertTriangle, ExternalLink, RefreshCw } from 'lucide-react'

/**
 * /cafe24-reauth — Cafe24 연동이 끊긴(refresh_token 폐기) 고객에게 재연동을 안내한다.
 *
 * 이 화면이 필요한 이유:
 * refresh_token 이 폐기되면 서버는 access_token 을 더 이상 갱신할 수 없고, 그때부터
 * 주문 웹훅에서 주문 상세를 못 읽어 Meta/Mixpanel 이벤트가 통째로 유실된다. 복구 경로는
 * 고객 재연동뿐이라 서버가 자력으로 살릴 방법이 없다.
 *
 * 그런데 폐기된 몰은 access_token 도 곧 만료돼서 대시보드 API 가 전부 401 로 떨어진다
 * (2026-08-02 실측: 폐기 상태 17개 몰 전부 access_token 만료). 즉 홈 화면 배너로는
 * 이 고객들에게 도달할 수 없다 — 홈이 그려지질 않는다. 그래서 401 을 받는 이 지점이
 * 안내를 붙일 수 있는 유일한 자리다.
 *
 * 종전에는 같은 401 이 "세션이 만료되었어요. Cafe24 관리자 페이지에서 다시 접속해주세요"
 * 토스트로 처리돼서, 원인(연동 끊김)도 영향(데이터 유실)도 알려주지 않았다.
 *
 * 진입은 `lib/auth.ts:handleCafe24ReauthRequired` 가 건다.
 */
export const Route = createFileRoute('/cafe24-reauth')({
  component: Cafe24ReauthPage,
  validateSearch: (
    search: Record<string, unknown>,
  ): { mall_id?: string; shop_no?: number } => ({
    mall_id: typeof search.mall_id === 'string' ? search.mall_id : undefined,
    shop_no: Number(search.shop_no) || undefined,
  }),
})

/** 카페24 관리자 로그인 — mall_id 를 모를 때의 대체 진입 경로. */
const CAFE24_ADMIN_LOGIN_URL = 'https://eclogin.cafe24.com/'

function Cafe24ReauthPage() {
  const { mall_id: mallId, shop_no: shopNo } = Route.useSearch()

  // 원클릭 재연동은 **어느 샵인지 확실할 때만** 제공한다. shop_no 를 추측해서 보내면
  // 멀티샵 몰에서 엉뚱한 샵을 재연동시키고 고객은 성공한 줄 알고 떠난다.
  const canOneClickReauth = Boolean(mallId) && shopNo !== undefined

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface px-4 py-8">
      <div className="max-w-lg w-full bg-surface-lowest rounded-2xl border border-border p-6 sm:p-8">
        {/* 헤더 — 심각도를 먼저 알린다 (수집이 조용히 멈춘 상태) */}
        <div className="text-center mb-6">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-error/10">
            <AlertTriangle className="h-6 w-6 text-error" />
          </div>
          <h1 className="text-lg font-bold text-on-surface mb-2">
            카페24 연동이 끊겼어요
          </h1>
          <p className="text-sm text-on-surface-variant leading-relaxed">
            지금{' '}
            <span className="font-semibold text-on-surface">
              주문 데이터가 수집되지 않고 있어요.
            </span>
            <br />
            다시 연동해야 수집이 재개돼요.
          </p>
        </div>

        {/* 무엇이 멈췄는지 — 재연동을 미루면 무엇을 잃는지 구체적으로 알린다 */}
        <div className="mb-6 rounded-lg border border-border bg-surface p-4">
          <p className="text-sm font-medium text-on-surface mb-2">
            연동이 끊긴 동안 멈추는 것
          </p>
          <ul className="space-y-1.5 text-sm text-on-surface-variant leading-relaxed">
            <li>• 구매·취소 이벤트 수집 (메타 광고 성과 측정)</li>
            <li>• 주문 데이터 기반 분석 리포트</li>
          </ul>
          <p className="mt-3 text-xs text-on-surface-variant leading-relaxed">
            연동이 끊긴 기간의 데이터는{' '}
            <span className="font-medium">복구할 수 없어요.</span> 빠르게 다시
            연동할수록 유실이 줄어요.
          </p>
        </div>

        {/* 1차 경로 — 대상 샵이 확실할 때만 원클릭 (클릭 한 번에 OAuth) */}
        {canOneClickReauth && mallId && shopNo !== undefined ? (
          <button
            type="button"
            onClick={() => authApi.startOAuthWithMallId(mallId, shopNo)}
            className="flex min-h-[44px] items-center justify-center gap-2 w-full rounded-lg bg-primary text-on-primary py-3 text-sm font-medium hover:opacity-90 transition-opacity"
          >
            <RefreshCw className="h-4 w-4" />
            카페24 다시 연동하기
          </button>
        ) : (
          // 대상 샵을 특정할 수 없는 경우(파라미터 없이 직접 진입 등) — 카페24 관리자에서
          // 앱을 열면 그 경로가 OAuth 를 태워 자동으로 재연동된다. 추측해서 보내지 않는다.
          <a
            href={CAFE24_ADMIN_LOGIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex min-h-[44px] items-center justify-center gap-2 w-full rounded-lg bg-primary text-on-primary py-3 text-sm font-medium hover:opacity-90 transition-opacity"
          >
            카페24 관리자에서 앱 열기
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}

        {mallId && (
          <p className="mt-3 text-center text-xs text-on-surface-variant">
            연동 대상: <span className="font-mono">{mallId}</span>
          </p>
        )}

        {/* 보조 경로 — 위 버튼이 막히는 환경(팝업 차단·다른 계정 로그인 등) 대비 */}
        {canOneClickReauth && (
          <a
            href={CAFE24_ADMIN_LOGIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 flex min-h-[44px] items-center justify-center gap-2 w-full rounded-lg border border-border bg-surface py-3 text-sm font-medium text-on-surface-variant transition-colors hover:bg-surface-low"
          >
            카페24 관리자 페이지에서 앱 열기
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
    </div>
  )
}
